import _ from "lodash";

import AppConstants from "../constants/AppConstants";
import { SUB_PROP_GROUPS } from "@showitinc/merge-utils";
import { expandPropsList } from "../utils/Merge/MergeUtils.mjs";
import {
  STATE_TRANSITION_IN,
  STATE_TRANSITION_OUT,
  TRANSITION_IN,
  TRANSITION_OUT,
} from "../constants/AnimationConstants";

const PROPS_BASIC = ["hide", "nature", "locking", "snap", "direction", "spacing"];
const PROPS_BACKGROUND = ["background"];
const PROPS_TRANSITION = [TRANSITION_IN, TRANSITION_OUT];
const PROPS_DEFAULT_STATE_TRANSITION = ["aav", STATE_TRANSITION_IN, STATE_TRANSITION_OUT];

const LAYOUT_SPECIFIC_PROPS = ["background"];

const {
  BG_PROPERTY_BG_COLOR,
  BG_PROPERTY_FILL_TYPE,
  BG_PROPERTY_GRADIENT_ROTATION,
  BG_PROPERTY_GRADIENT_COLOR_1,
  BG_PROPERTY_GRADIENT_COLOR_2,
  BG_PROPERTY_LOOP,
  BG_PROPERTY_POSITION,
  BG_PROPERTY_OPACITY,
  BG_PROPERTY_SCALE,
  BG_PROPERTY_SCROLL,
  BG_PROPERTY_IMAGE,
  BG_PROPERTY_VIDEO,
  BG_PROPERTY_MEDIA_TYPE,
} = AppConstants;

const V2_BACKGROUND_PROPS = [
  BG_PROPERTY_BG_COLOR,
  BG_PROPERTY_FILL_TYPE,
  BG_PROPERTY_GRADIENT_ROTATION,
  BG_PROPERTY_GRADIENT_COLOR_1,
  BG_PROPERTY_GRADIENT_COLOR_2,
  BG_PROPERTY_LOOP,
  BG_PROPERTY_POSITION,
  BG_PROPERTY_OPACITY,
  BG_PROPERTY_SCALE,
  BG_PROPERTY_SCROLL,
  BG_PROPERTY_IMAGE,
  BG_PROPERTY_VIDEO,
  BG_PROPERTY_MEDIA_TYPE,
];

/**
 * Syncs on a V2 prop, deleting from mobile layout on the block or state and deleting from props to sync
 * This will be replaced with a utility later.
 * @param {Object} blockOrState - JS object containing layouts and possibly sync
 * @param {Object} props - Object key/val of props to be copied to blockOrState
 * @param {String} syncProp - prop to sync on
 */
export const syncV2Props = (blockOrState, props, syncProp) => {
  const { sync } = blockOrState;

  blockOrState.sync = sync ? (!sync.includes(syncProp) ? [...sync, syncProp] : sync) : [syncProp];


  expandPropsList(blockOrState, [syncProp]).forEach((key) => {
    delete blockOrState.mobile[key];
    delete props[key];
  });
};

class BlockPropertyCopier {
  constructor(/** String **/ fromLayout, /** String **/ toLayout) {
    this.fromLayout = fromLayout;
    this.toLayout = toLayout;

    this.propModifierMap = {
      background: this._modifyBackground,
    };
  }

  copy(/** Object **/ block, /** Object **/ config = {}, ...restProps) {
    const includeStates = _.get(config, "includeStates", false);

    const props = restProps.length
      ? restProps
      : _.concat(
          PROPS_BASIC,
          PROPS_BACKGROUND,
          PROPS_TRANSITION,
          PROPS_DEFAULT_STATE_TRANSITION,
          V2_BACKGROUND_PROPS
        );

    this._updateProps(block, props);

    if (includeStates) {
      const states = _.get(block, "states", []);

      for (let i = 0; i < states.length; i++) {
        this._updateProps(states[i], props);
      }
    }

    return block;
  }

  _updateProps(/** Object **/ blockOrState, /** Array **/ props) {
    // make sure to clone all props so if they are modified it doesn't affect the other layout
    let toCopyProps = _.cloneDeep(_.pick(_.get(blockOrState, this.fromLayout), props));

    const hasLayoutSpecificProps = _.some(LAYOUT_SPECIFIC_PROPS, (prop) =>
      _.has(toCopyProps, prop)
    );

    if (props.includes(BG_PROPERTY_FILL_TYPE)) {
      syncV2Props(blockOrState, toCopyProps, BG_PROPERTY_FILL_TYPE);
    }
    if (props.includes(BG_PROPERTY_MEDIA_TYPE)) {
      syncV2Props(blockOrState, toCopyProps, BG_PROPERTY_MEDIA_TYPE);
    }

    if (hasLayoutSpecificProps) {
      toCopyProps = this._modifyPropsForLayout(
        toCopyProps,
        LAYOUT_SPECIFIC_PROPS,
        this.propModifierMap,
        this.toLayout
      );
    }

    _.merge(_.get(blockOrState, this.toLayout), toCopyProps);

    return blockOrState;
  }

  _modifyPropsForLayout(
    /** Object **/ allProps,
    /** Array **/ propNames,
    /** Object **/ modifierMap,
    /** String **/ toLayout
  ) {
    const props = _.pick(allProps, propNames);

    _.each(props, (value, key) => {
      if (_.has(modifierMap, key)) {
        _.set(allProps, key, _.get(modifierMap, key)(value, toLayout));
      }
    });

    return allProps;
  }

  _modifyBackground(background, toLayout) {
    switch (toLayout) {
      case AppConstants.LAYOUT_MOBILE:
        break;
      default:
    }

    return background;
  }
}

export default BlockPropertyCopier;
export const BlockConstants = {
  PROPS_BASIC,
  PROPS_BACKGROUND,
  PROPS_TRANSITION,
  PROPS_DEFAULT_STATE_TRANSITION,
};
